import React, { useMemo, useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { getInvoices, pushInvoiceToXero, pullInvoicesFromXero, getXeroStatus, markInvoiceSent, fetchInvoicePdfUrl, deleteInvoice, getCachedPreference, setUserPreference, getUsers, createManualDraftInvoice } from '../../services/api';
import { PageHeader, StatusBadge } from '../../components/Shared';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Download, Upload, RefreshCw, CheckCircle2, Send, Lock, ExternalLink, FileEdit, Trash2, ArrowUp, ArrowDown, ArrowUpDown, Search, X, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { PdfPreviewDialog } from '../../components/PdfPreviewDialog';
import { usePdfPreview } from '../../hooks/usePdfPreview';
import { fmtDate } from '../../components/SortHeader';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '../../components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';

const AdminInvoices = () => {
  const navigate = useNavigate();
  const pdf = usePdfPreview();
  const [list, setList] = useState([]);
  const [xeroConnected, setXeroConnected] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [pushingId, setPushingId] = useState(null);
  const [sendingId, setSendingId] = useState(null);
  // Delete dialog state: { inv, voidXero, busy } | null
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [voidXero, setVoidXero] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // "New invoice" dialog state — customer picker + issued/due dates.
  const [createOpen, setCreateOpen] = useState(false);
  // Free-text search across invoice number (Xero or internal),
  // customer, and reference. Case-insensitive substring match — kept
  // simple so admins can paste a partial Xero number like
  // `0027` or `IN20260002` and find it instantly.
  const [search, setSearch] = useState('');
  // Set of invoice IDs the most recent Xero sync just imported. We pin
  // these to the top of the list (regardless of the chosen sort) and
  // tag them with a "Just imported" pill so admins can immediately spot
  // newly arrived records. Cleared by the next sync.
  const [justImportedIds, setJustImportedIds] = useState(() => new Set());
  // When true, hide every invoice that's not in `justImportedIds` so
  // the admin can isolate the rows from the latest sync. Auto-cleared
  // when the import set is empty (next sync brought zero).
  const [onlyJustImported, setOnlyJustImported] = useState(false);
  useEffect(() => {
    if (justImportedIds.size === 0 && onlyJustImported) setOnlyJustImported(false);
  }, [justImportedIds, onlyJustImported]);
  // Sort state — persisted via the user-preferences API so the same admin
  // gets the same ordering on phone + laptop. Stored as `"key:dir"` string
  // to match the backend's string-only preference schema. `getCachedPreference`
  // is read synchronously from the localStorage-backed cache, so first paint
  // already has the right sort. Default: newest-invoice-first by number.
  const INVOICE_SORT_KEY = 'sort.invoices';
  const parseSort = (raw) => {
    if (typeof raw !== 'string') return null;
    const [key, dir] = raw.split(':');
    if (!key || (dir !== 'asc' && dir !== 'desc')) return null;
    return { key, dir };
  };
  const [sort, setSort] = useState(() => {
    const cached = getCachedPreference(INVOICE_SORT_KEY);
    const parsed = parseSort(cached);
    if (parsed) return parsed;
    // Legacy migration: older builds wrote an object via sessionStorage.
    try {
      const legacy = sessionStorage.getItem(INVOICE_SORT_KEY);
      if (legacy) {
        const obj = JSON.parse(legacy);
        if (obj && obj.key && obj.dir) return obj;
      }
    } catch { /* ignore */ }
    return { key: 'number', dir: 'desc' };
  });
  // Skip the initial render — we already pulled the persisted value above
  // and don't want to write it straight back to the API.
  const isFirstSortRender = React.useRef(true);
  useEffect(() => {
    if (isFirstSortRender.current) {
      isFirstSortRender.current = false;
      return;
    }
    setUserPreference(INVOICE_SORT_KEY, `${sort.key}:${sort.dir}`);
  }, [sort]);
  const toggleSort = (key) => setSort((prev) => (
    prev.key === key
      ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: key === 'amount' || key === 'issued' || key === 'due' ? 'desc' : 'asc' }
  ));
  const [searchParams] = useSearchParams();
  // URL-driven filters — deep-linked from customer detail stat tiles.
  // `status=outstanding` = sent/authorised and not-yet-due; `status=overdue`
  // = sent/authorised with a dueDate < today; undefined → show everything.
  const customerIdFilter = searchParams.get('customerId') || '';
  const bucket = searchParams.get('status') || '';

  const refresh = () => getInvoices().then(setList);

  useEffect(() => {
    refresh();
    getXeroStatus().then(s => setXeroConnected(!!s?.connected)).catch(() => {});
  }, []);

  const displayNumber = (inv) => inv.xeroInvoiceNumber || inv.id;

  const openPdf = (inv) => pdf.preview(() => fetchInvoicePdfUrl(inv.id));

  const markSent = async (inv) => {
    const num = displayNumber(inv);
    if (!window.confirm(`Lock invoice ${num}? Future work orders for ${inv.customer} will start a new invoice.`)) return;
    setSendingId(inv.id);
    try {
      await markInvoiceSent(inv.id, true);
      toast.success(`${num} locked and marked as sent in Xero`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to mark sent');
    } finally {
      setSendingId(null);
    }
  };

  const pushOne = async (inv) => {
    setPushingId(inv.id);
    try {
      const res = await pushInvoiceToXero(inv.id);
      // The push response carries the freshly-assigned Xero invoice number,
      // so prefer that over the local one for the confirmation toast.
      const num = res?.xeroInvoiceNumber || displayNumber(inv);
      toast.success(`${num} \u2192 Xero (${res.xeroStatus})`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Push failed');
    } finally {
      setPushingId(null);
    }
  };

  const deleteOne = (inv, e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    setVoidXero(false);
    setDeleteTarget(inv);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const inv = deleteTarget;
    const num = displayNumber(inv);
    setDeleting(true);
    try {
      const res = await deleteInvoice(inv.id, { voidXero: voidXero && !!inv.xeroInvoiceId });
      const xeroStatus = res?.xero?.status;
      if (xeroStatus) {
        toast.success(`${num} deleted · Xero ${xeroStatus.toLowerCase()}`);
      } else {
        toast.success(`${num} deleted`);
      }
      setDeleteTarget(null);
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const syncAll = async () => {
    setSyncing(true);
    try {
      const res = await pullInvoicesFromXero();
      toast.success(`Sync: ${res.imported || 0} imported · ${res.updated || 0} updated · ${res.scanned || 0} scanned`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const filtered = useMemo(() => {
    const isOpenStatus = (inv) => {
      const s = (inv.status || '').toLowerCase();
      const xs = (inv.xeroStatus || '');
      if (s === 'paid' || s === 'voided' || s === 'deleted') return false;
      if (xs === 'PAID' || xs === 'VOIDED' || xs === 'DELETED') return false;
      return (
        ['sent', 'authorised', 'submitted', 'overdue', 'partially-paid'].includes(s)
        || ['AUTHORISED', 'SUBMITTED'].includes(xs)
      );
    };
    return list.filter((i) => {
      if (onlyJustImported && !justImportedIds.has(i.id)) return false;
      if (customerIdFilter && i.customerId !== customerIdFilter) return false;
      if (bucket === 'draft') {
        // Drafts: review bucket — anything still in status=draft
        // (pre-send) OR xeroStatus=DRAFT if it came from Xero.
        const isDraft = (i.status || '').toLowerCase() === 'draft'
          || (i.xeroStatus || '') === 'DRAFT';
        if (!isDraft) return false;
        if (i.sent) return false;
      }
      if (bucket === 'outstanding' || bucket === 'overdue') {
        if (!isOpenStatus(i)) return false;
        const d = i.dueDate ? new Date(i.dueDate) : null;
        const isOverdue = d && !isNaN(d.getTime()) && d.getTime() < Date.now();
        if (bucket === 'overdue' && !isOverdue) return false;
      }
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        const haystack = [
          i.xeroInvoiceNumber, i.id, i.customer, i.reference,
        ].filter(Boolean).join(' ').toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [list, customerIdFilter, bucket, search, onlyJustImported, justImportedIds]);

  // Apply the user's chosen sort on top of the filtered rows. Natural-numeric
  // compare for invoice numbers (INV-2501 < INV-2510), locale compare for
  // customer names, numeric for amount, date-parse for issued/due.
  const sorted = useMemo(() => {
    const dir = sort.dir === 'asc' ? 1 : -1;
    const val = (inv) => {
      switch (sort.key) {
        case 'customer': return (inv.customer || '').toLowerCase();
        case 'amount': return Number(inv.amount) || 0;
        case 'issued': return inv.issuedAt ? new Date(inv.issuedAt).getTime() : 0;
        case 'due': return inv.dueDate ? new Date(inv.dueDate).getTime() : 0;
        case 'number':
        default: {
          const s = String(inv.xeroInvoiceNumber || inv.id || '');
          return s;
        }
      }
    };
    const cmp = (a, b) => {
      // Always pin freshly-synced invoices to the very top, regardless
      // of the active sort direction or column. Within the just-imported
      // group, fall through to the normal sort so the pinned block is
      // still ordered consistently.
      const ai = justImportedIds.has(a.id) ? 0 : 1;
      const bi = justImportedIds.has(b.id) ? 0 : 1;
      if (ai !== bi) return ai - bi;
      const va = val(a); const vb = val(b);
      if (typeof va === 'string' && typeof vb === 'string') {
        return va.localeCompare(vb, undefined, { numeric: true, sensitivity: 'base' }) * dir;
      }
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    };
    return [...filtered].sort(cmp);
  }, [filtered, sort, justImportedIds]);

  // Counts for the quick-filter pills at the top. Cheap — just a .filter().
  const draftCount = useMemo(
    () => list.filter(i => ((i.status || '').toLowerCase() === 'draft' || (i.xeroStatus || '') === 'DRAFT') && !i.sent).length,
    [list],
  );
  const draftTotal = useMemo(
    () => list
      .filter(i => ((i.status || '').toLowerCase() === 'draft' || (i.xeroStatus || '') === 'DRAFT') && !i.sent)
      .reduce((s, i) => s + (Number(i.amount) || 0), 0),
    [list],
  );

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <PageHeader
        title="Invoices"
        description="Customer billing & payment status. Push to Xero or sync statuses back with one click."
        actions={
          <div className="flex items-center gap-2">
            <Button
              onClick={() => setCreateOpen(true)}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="new-invoice-btn"
            >
              <Plus className="h-4 w-4 mr-1.5" /> New invoice
            </Button>
            {xeroConnected && (
              <Button variant="outline" onClick={syncAll} disabled={syncing} data-testid="invoices-sync-xero-btn">
                <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? 'Syncing...' : 'Sync from Xero'}
              </Button>
            )}
          </div>
        }
      />

      {!xeroConnected && (
        <div className="bg-amber-50 border border-amber-200 rounded-md px-4 py-3 text-sm text-amber-900">
          Xero not connected. <a href="/admin/xero" className="font-medium underline">Connect Xero</a> to push invoices and sync payment status.
        </div>
      )}

      {/* Quick-filter pills: All · Drafts · Outstanding · Overdue. URL-driven
          so admins can bookmark a filter (e.g. "Drafts to review"). On
          mobile the row is horizontally scrollable; the search input drops
          onto its own row and stretches full-width. */}
      <div className="-mx-1 flex items-center gap-2 overflow-x-auto pb-1 pl-1 pr-1 md:flex-wrap md:overflow-visible" data-testid="invoice-filter-pills">
        <FilterPill label="All invoices" to="/admin/invoices" active={!bucket} count={list.length} testId="filter-all" />
        <FilterPill
          label="Drafts to review"
          to="/admin/invoices?status=draft"
          active={bucket === 'draft'}
          count={draftCount}
          amount={draftTotal}
          icon={FileEdit}
          tone="violet"
          testId="filter-draft"
        />
        <FilterPill
          label="Outstanding"
          to="/admin/invoices?status=outstanding"
          active={bucket === 'outstanding'}
          testId="filter-outstanding"
        />
        <FilterPill
          label="Overdue"
          to="/admin/invoices?status=overdue"
          active={bucket === 'overdue'}
          tone="red"
          testId="filter-overdue"
        />
        {justImportedIds.size > 0 && (
          <button
            type="button"
            onClick={() => setOnlyJustImported((v) => !v)}
            data-testid="filter-just-imported-toggle"
            className={`shrink-0 whitespace-nowrap inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
              onlyJustImported
                ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm'
                : 'bg-emerald-50 text-emerald-800 border-emerald-200 hover:border-emerald-400'
            }`}
            title={onlyJustImported ? 'Show all invoices' : 'Show only the rows imported in the latest Xero sync'}
          >
            <span className={`h-1.5 w-1.5 rounded-full ${onlyJustImported ? 'bg-white' : 'bg-emerald-500'}`} />
            <span>Just imported</span>
            <span className={`tabular-nums ${onlyJustImported ? 'opacity-90' : 'text-emerald-700'}`}>
              {justImportedIds.size}
            </span>
            {onlyJustImported && <X className="h-3 w-3 ml-0.5" />}
          </button>
        )}
      </div>
      {/* Search-by-number — accepts partial Xero numbers
          (e.g. `0027`), full numbers (`IN202600027`), internal IDs
          (`INV-2814`), customer names, and references. Own row so it
          gets the full width on mobile and doesn't shove the filter
          chips out of the tap area. */}
      <div className="relative">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by number, customer, ref…"
          className="pl-9 pr-9 h-11 text-base md:h-9 md:text-sm bg-white"
          aria-label="Search invoices"
          data-testid="invoice-search-input"
          inputMode="search"
          autoComplete="off"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 p-1.5"
            aria-label="Clear search"
            data-testid="invoice-search-clear"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {bucket === 'draft' && draftCount > 0 && (
        <div className="bg-violet-50 border border-violet-200 rounded-md px-4 py-3 text-sm text-violet-900">
          Reviewing <strong>{draftCount}</strong> draft{draftCount === 1 ? '' : 's'} worth <strong>£{draftTotal.toFixed(2)}</strong>.
          Drafts accumulate as engineers clock out of customer sites &mdash; click a row to review the
          line-items, then use <strong>Lock &amp; mark sent</strong> when ready to invoice.
        </div>
      )}

      {/* Desktop / tablet table (≥md). Below md we render the card list below. */}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden hidden lg:block">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left">
              <SortTh sort={sort} sortKey="number" onClick={toggleSort} testId="sort-number">Invoice</SortTh>
              <SortTh sort={sort} sortKey="customer" onClick={toggleSort} testId="sort-customer">Customer</SortTh>
              <th className="px-4 py-3 font-medium text-slate-600 hidden lg:table-cell">Work orders</th>
              <SortTh sort={sort} sortKey="amount" onClick={toggleSort} testId="sort-amount" align="right md:left">Amount</SortTh>
              <SortTh sort={sort} sortKey="issued" onClick={toggleSort} testId="sort-issued" hiddenBelow="md">Issued</SortTh>
              <SortTh sort={sort} sortKey="due" onClick={toggleSort} testId="sort-due" hiddenBelow="lg">Due</SortTh>
              <th className="px-4 py-3 font-medium text-slate-600">Status</th>
              <th className="px-4 py-3 font-medium text-slate-600 hidden lg:table-cell">Xero</th>
              <th className="px-4 py-3 font-medium text-slate-600 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {sorted.map(i => {
              // Drafts open the editable detail page; sent/locked invoices
              // open the PDF (historical behaviour — the PDF is the
              // authoritative record once Xero has it).
              const isDraft = !i.sent && ((i.status || '').toLowerCase() === 'draft' || (i.xeroStatus || '') === 'DRAFT');
              const onRowClick = () => {
                // Always navigate to the invoice detail page. The eye
                // icon in the row's actions column is the explicit
                // "open PDF" shortcut — keeping the row tap reserved
                // for the editor (where Approve / Re-send / email
                // history live for non-draft invoices).
                window.location.href = `/admin/invoices/${i.uid || i.id}`;
              };
              return (
              <tr
                key={i.id}
                data-testid={`invoice-row-${i.id}`}
                onClick={onRowClick}
                className="hover:bg-slate-50 cursor-pointer"
                title={isDraft ? 'Click to open draft for review / edit' : 'Click to open invoice PDF'}
              >
                <td className="px-4 py-3 font-mono text-xs text-slate-700">
                  <div className="font-semibold text-slate-900">{i.xeroInvoiceNumber || i.id}</div>
                  {i.sent && <div className="mt-0.5 inline-flex items-center gap-1 text-[10px] text-slate-500"><Lock className="h-2.5 w-2.5" /> locked</div>}
                </td>
                <td className="px-4 py-3 font-medium text-slate-900">
                  <div className="truncate max-w-[160px]">{i.customer}</div>
                </td>
                <td className="px-4 py-3 text-xs text-slate-600 hidden lg:table-cell">
                  {(i.workOrders && i.workOrders.length > 0)
                    ? <span>{i.workOrders.length} WO · {i.workOrders.slice(0, 2).join(', ')}{i.workOrders.length > 2 ? '...' : ''}</span>
                    : <span className="text-slate-400">—</span>}
                </td>
                <td className="px-4 py-3 font-semibold text-slate-900 text-right md:text-left">£{(i.amount || 0).toFixed(2)}</td>
                <td className="px-4 py-3 text-slate-600 hidden md:table-cell">{fmtDate(i.issuedAt)}</td>
                <td className="px-4 py-3 text-slate-600 hidden lg:table-cell">{fmtDate(i.dueDate)}</td>
                <td className="px-4 py-3"><StatusBadge status={i.status} /></td>
                <td className="px-4 py-3 hidden lg:table-cell">
                  {i.xeroInvoiceId ? (
                    <a
                      href={`https://go.xero.com/AccountsReceivable/Edit.aspx?InvoiceID=${i.xeroInvoiceId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`xero-link-${i.id}`}
                      className="inline-flex items-center gap-1 text-xs text-blue-800 font-medium hover:text-blue-600 hover:underline transition-colors"
                      title="Open this invoice in Xero"
                    >
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      <span>{i.xeroStatus || 'Synced'}</span>
                      <ExternalLink className="h-3 w-3 opacity-60" />
                    </a>
                  ) : (
                    <span className="text-xs text-slate-400">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="inline-flex items-center gap-1">
                    {xeroConnected && !i.sent && ((i.status || '').toLowerCase() === 'draft' || (i.xeroStatus || '') === 'DRAFT') && (
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => markSent(i)}
                        disabled={sendingId === i.id}
                        title="Lock this draft so new WOs start a fresh invoice"
                        data-testid={`mark-sent-${i.id}`}
                      >
                        <Send className={`h-4 w-4 ${sendingId === i.id ? 'animate-pulse' : ''}`} />
                      </Button>
                    )}
                    {xeroConnected && (
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => pushOne(i)}
                        disabled={pushingId === i.id}
                        title={i.xeroInvoiceId ? 'Update in Xero' : 'Push to Xero'}
                        data-testid={`push-invoice-${i.id}`}
                      >
                        <Upload className={`h-4 w-4 ${pushingId === i.id ? 'animate-pulse' : ''}`} />
                      </Button>
                    )}
                    <Button
                      size="sm" variant="ghost"
                      onClick={() => openPdf(i)}
                      title="Download PDF"
                      data-testid={`download-invoice-${i.id}`}
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm" variant="ghost"
                      onClick={(e) => deleteOne(i, e)}
                      title="Delete invoice"
                      className="text-red-600 hover:text-red-800"
                      data-testid={`delete-invoice-${i.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </td>
              </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-12 text-center text-slate-500">No invoices yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile card list (<md). Each invoice is a full-width card with
          amount on the right, status + actions below — no horizontal scroll. */}
      <div className="lg:hidden space-y-3">
        {/* Mobile sort control — desktop uses clickable column headers. */}
        <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-md px-3 py-2">
          <label htmlFor="mobile-invoice-sort" className="text-xs text-slate-500 shrink-0">Sort by</label>
          <select
            id="mobile-invoice-sort"
            className="flex-1 h-8 text-sm border border-slate-200 rounded px-2"
            value={`${sort.key}:${sort.dir}`}
            onChange={(e) => {
              const [key, dir] = e.target.value.split(':');
              setSort({ key, dir });
            }}
            data-testid="mobile-sort-select"
          >
            <option value="number:desc">Invoice number — newest first</option>
            <option value="number:asc">Invoice number — oldest first</option>
            <option value="customer:asc">Customer (A–Z)</option>
            <option value="customer:desc">Customer (Z–A)</option>
            <option value="amount:desc">Amount — high to low</option>
            <option value="amount:asc">Amount — low to high</option>
            <option value="issued:desc">Issued date — newest</option>
            <option value="issued:asc">Issued date — oldest</option>
            <option value="due:desc">Due date — latest</option>
            <option value="due:asc">Due date — soonest</option>
          </select>
        </div>
        {sorted.map(i => {
          const isDraft = !i.sent && ((i.status || '').toLowerCase() === 'draft' || (i.xeroStatus || '') === 'DRAFT');
          const onCardClick = () => {
            // Always navigate to detail — the eye icon button below
            // remains as the explicit "open PDF" shortcut.
            window.location.href = `/admin/invoices/${i.uid || i.id}`;
          };
          return (
          <div
            key={i.id}
            data-testid={`invoice-card-${i.id}`}
            className="bg-white border border-slate-200 rounded-md p-4 space-y-3 active:bg-slate-50"
            onClick={onCardClick}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="font-mono text-xs font-semibold text-slate-900 truncate flex items-center gap-2 flex-wrap">
                  <span className="truncate">{i.xeroInvoiceNumber || i.id}</span>
                  {justImportedIds.has(i.id) && (
                    <span
                      className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 px-1.5 py-0.5 text-[9px] font-medium tracking-wide uppercase shrink-0"
                      data-testid={`just-imported-pill-mobile-${i.id}`}
                      title="Just imported from this Xero sync"
                    >
                      <span className="h-1 w-1 rounded-full bg-emerald-500"></span>
                      New
                    </span>
                  )}
                </div>
                <div className="text-sm font-medium text-slate-900 truncate mt-0.5">{i.customer}</div>
                {i.sent && (
                  <div className="mt-1 inline-flex items-center gap-1 text-[10px] text-slate-500">
                    <Lock className="h-2.5 w-2.5" /> locked
                  </div>
                )}
              </div>
              <div className="text-right shrink-0">
                <div className="font-display text-base text-slate-900 tabular-nums">
                  £{(i.amount || 0).toFixed(2)}
                </div>
                <div className="mt-1"><StatusBadge status={i.status} /></div>
              </div>
            </div>
            <div className="flex items-center justify-between gap-2 text-[11px] text-slate-500">
              <div className="flex items-center gap-3 min-w-0">
                {i.issuedAt && <span className="truncate">Issued {fmtDate(i.issuedAt)}</span>}
                {i.xeroInvoiceId && (
                  <a
                    href={`https://go.xero.com/AccountsReceivable/Edit.aspx?InvoiceID=${i.xeroInvoiceId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-1 text-blue-700 hover:underline shrink-0"
                  >
                    <CheckCircle2 className="h-3 w-3" />
                    Xero
                    <ExternalLink className="h-2.5 w-2.5 opacity-60" />
                  </a>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                {xeroConnected && !i.sent && ((i.status || '').toLowerCase() === 'draft' || (i.xeroStatus || '') === 'DRAFT') && (
                  <Button
                    size="sm" variant="ghost"
                    onClick={() => markSent(i)}
                    disabled={sendingId === i.id}
                    data-testid={`mark-sent-mobile-${i.id}`}
                  >
                    <Send className={`h-4 w-4 ${sendingId === i.id ? 'animate-pulse' : ''}`} />
                  </Button>
                )}
                {xeroConnected && (
                  <Button
                    size="sm" variant="ghost"
                    onClick={() => pushOne(i)}
                    disabled={pushingId === i.id}
                    data-testid={`push-invoice-mobile-${i.id}`}
                  >
                    <Upload className={`h-4 w-4 ${pushingId === i.id ? 'animate-pulse' : ''}`} />
                  </Button>
                )}
                <Button
                  size="sm" variant="ghost"
                  onClick={() => openPdf(i)}
                  data-testid={`download-invoice-mobile-${i.id}`}
                >
                  <Download className="h-4 w-4" />
                </Button>
                <Button
                  size="sm" variant="ghost"
                  onClick={(e) => deleteOne(i, e)}
                  className="text-red-600 hover:text-red-800"
                  data-testid={`delete-invoice-mobile-${i.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="bg-white border border-slate-200 rounded-md px-4 py-12 text-center text-slate-500">
            {bucket === 'draft' ? 'No drafts to review.'
              : bucket === 'outstanding' ? 'No outstanding invoices.'
              : bucket === 'overdue' ? 'No overdue invoices.'
              : 'No invoices yet.'}
          </div>
        )}
      </div>

      <PdfPreviewDialog
        url={pdf.url}
        filename={pdf.filename}
        onClose={pdf.close}
        title="Invoice preview"
      />

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(o) => { if (!o && !deleting) setDeleteTarget(null); }}
      >
        <AlertDialogContent data-testid="delete-invoice-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete invoice {deleteTarget ? displayNumber(deleteTarget) : ''}?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-slate-600">
                <p>
                  This removes the invoice locally and frees up every work-order
                  labour entry and part it billed so they can be re-billed.
                </p>
                {deleteTarget?.xeroInvoiceId ? (
                  <p className="text-slate-700">
                    This invoice is linked to Xero ({deleteTarget.xeroInvoiceNumber || deleteTarget.xeroInvoiceId}).
                    By default only the local copy is removed.
                  </p>
                ) : (
                  <p className="text-slate-500">Not linked to Xero.</p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>

          {deleteTarget?.xeroInvoiceId && (
            <label className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 cursor-pointer">
              <Checkbox
                id="void-xero-too"
                checked={voidXero}
                onCheckedChange={(v) => setVoidXero(!!v)}
                disabled={deleting}
                data-testid="void-xero-checkbox"
              />
              <span className="text-sm leading-snug text-amber-900">
                <span className="font-medium">Also void/delete in Xero</span>
                <span className="block text-xs text-amber-800 mt-0.5">
                  Draft → deleted · Authorised → voided · Paid invoices can’t be voided (raise a credit note in Xero).
                </span>
              </span>
            </label>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={deleting}
              data-testid="delete-invoice-cancel"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmDelete(); }}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="delete-invoice-confirm"
            >
              {deleting ? 'Deleting…' : 'Delete invoice'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <NewInvoiceDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(inv) => {
          setCreateOpen(false);
          toast.success(`Invoice ${inv.xeroInvoiceNumber || inv.id} created`);
          // Push the new invoice into the local list so the user sees it
          // even if they navigate back — the detail page is where they
          // actually finish the invoice, so we redirect immediately.
          setList(l => [inv, ...l]);
          navigate(`/admin/invoices/${inv.uid || inv.id}`);
        }}
      />
    </div>
  );
};

/**
 * "New invoice" dialog. Picks a customer + issued/due dates and hits
 * `POST /invoices/draft`. The heavy work (line items, discounts, push
 * to Xero) happens on the detail page we redirect to.
 */
const NewInvoiceDialog = ({ open, onClose, onCreated }) => {
  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [customerId, setCustomerId] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');
  const todayIso = new Date().toISOString().slice(0, 10);
  const [issuedAt, setIssuedAt] = useState(todayIso);
  const [dueDate, setDueDate] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Reset for a clean slate every time.
    setCustomerId('');
    setCustomerFilter('');
    setIssuedAt(todayIso);
    setDueDate('');
    setLoadingCustomers(true);
    getUsers()
      .then(rows => setCustomers(
        (rows || [])
          .filter(u => u.role === 'customer' && !u.parentCustomerId)
          .sort((a, b) => (a.company || a.name || '').localeCompare(b.company || b.name || '')),
      ))
      .catch(() => toast.error('Could not load customers'))
      .finally(() => setLoadingCustomers(false));
  }, [open]);

  const filteredCustomers = useMemo(() => {
    const q = customerFilter.trim().toLowerCase();
    if (!q) return customers.slice(0, 200);
    return customers.filter(c =>
      (c.company || '').toLowerCase().includes(q)
      || (c.name || '').toLowerCase().includes(q)
      || (c.email || '').toLowerCase().includes(q),
    ).slice(0, 200);
  }, [customers, customerFilter]);

  const create = async () => {
    if (!customerId) return toast.error('Pick a customer');
    setBusy(true);
    try {
      const inv = await createManualDraftInvoice({
        customerId,
        issuedAt: issuedAt || undefined,
        dueDate: dueDate || undefined,
      });
      onCreated(inv);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create invoice');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="new-invoice-dialog">
        <DialogHeader>
          <DialogTitle>New invoice</DialogTitle>
          <DialogDescription>
            Creates an empty draft. You&apos;ll add line items and push to Xero on the next page.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Customer</Label>
            <Input
              value={customerFilter}
              onChange={e => setCustomerFilter(e.target.value)}
              placeholder="Search by company, name or email…"
              className="mt-1"
              data-testid="new-invoice-customer-filter"
            />
            <div className="mt-1 border border-slate-200 rounded max-h-56 overflow-y-auto bg-white">
              {loadingCustomers ? (
                <div className="text-xs text-slate-500 p-3">Loading customers…</div>
              ) : filteredCustomers.length === 0 ? (
                <div className="text-xs text-slate-500 p-3">No customers match &quot;{customerFilter}&quot;.</div>
              ) : filteredCustomers.map(c => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setCustomerId(c.id)}
                  className={`w-full text-left px-3 py-2 text-sm border-b border-slate-100 last:border-b-0 transition-colors ${customerId === c.id ? 'bg-emerald-50 text-emerald-900' : 'hover:bg-slate-50'}`}
                  data-testid={`new-invoice-customer-${c.id}`}
                >
                  <div className="font-medium truncate">{c.company || c.name || c.email}</div>
                  <div className="text-[11px] text-slate-500 truncate">{c.email || '—'}</div>
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Issue date</Label>
              <Input
                type="date"
                value={issuedAt}
                onChange={e => setIssuedAt(e.target.value)}
                className="mt-1"
                data-testid="new-invoice-issued"
              />
            </div>
            <div>
              <Label className="text-xs">Due date</Label>
              <Input
                type="date"
                value={dueDate}
                min={issuedAt || undefined}
                onChange={e => setDueDate(e.target.value)}
                placeholder="From payment terms"
                className="mt-1"
                data-testid="new-invoice-due"
              />
              <div className="text-[10px] text-slate-500 mt-0.5">
                Leave empty to use the customer&apos;s payment terms.
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="new-invoice-cancel">Cancel</Button>
          <Button
            onClick={create}
            disabled={busy || !customerId}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="new-invoice-create"
          >
            {busy ? 'Creating…' : 'Create draft'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Sortable table header. Clicking toggles asc/desc, clicking a different
// column switches sort key. Keeps its own arrow icon to signal state.
const SortTh = ({ children, sort, sortKey, onClick, testId, align, hiddenBelow }) => {
  const active = sort.key === sortKey;
  const Icon = !active ? ArrowUpDown : (sort.dir === 'asc' ? ArrowUp : ArrowDown);
  const alignClass = align === 'right md:left' ? 'text-right md:text-left' : '';
  const hideClass = hiddenBelow === 'md' ? 'hidden md:table-cell'
    : hiddenBelow === 'lg' ? 'hidden lg:table-cell' : '';
  return (
    <th className={`px-4 py-3 font-medium text-slate-600 ${alignClass} ${hideClass}`}>
      <button
        type="button"
        onClick={() => onClick(sortKey)}
        className={`inline-flex items-center gap-1 hover:text-slate-900 ${active ? 'text-slate-900' : ''}`}
        data-testid={testId}
      >
        <span>{children}</span>
        <Icon className={`h-3 w-3 ${active ? 'opacity-100' : 'opacity-40'}`} />
      </button>
    </th>
  );
};


const FilterPill = ({ label, to, active, count, amount, icon: Icon, tone, testId }) => {
  const toneClass = active
    ? tone === 'violet' ? 'bg-violet-600 text-white border-violet-600'
    : tone === 'red' ? 'bg-red-600 text-white border-red-600'
    : 'bg-slate-900 text-white border-slate-900'
    : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400';
  return (
    <Link
      to={to}
      data-testid={testId}
      className={`shrink-0 whitespace-nowrap inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${toneClass}`}
    >
      {Icon && <Icon className="h-3.5 w-3.5" />}
      <span>{label}</span>
      {count !== undefined && (
        <span className={`tabular-nums ${active ? 'opacity-90' : 'text-slate-500'}`}>
          {count}
          {amount ? ` · £${amount.toFixed(0)}` : ''}
        </span>
      )}
    </Link>
  );
};

export default AdminInvoices;
